<script lang="ts" module>
  export type { SheetAction } from '../../lib/sheetActions';
</script>

<script lang="ts">
  /**
   * ActionSheet — mobile action menu (plans/mobile-v2.md §3.4): a bottom sheet
   * with a vertical list of ≥48px rows, iOS action-sheet style. Selecting a row
   * closes the sheet, then runs the action.
   */
  import type { SheetAction } from '../../lib/sheetActions';
  import MobileSheet from '../MobileSheet.svelte';
  import Icon from '../Icon.svelte';

  interface Props {
    open: boolean;
    onClose: () => void;
    /** Shown as the sheet title (e.g. the project the actions apply to). */
    title?: string;
    /** Accessible name when there is no title. */
    label?: string;
    actions: SheetAction[];
  }

  let { open, onClose, title, label, actions }: Props = $props();

  function select(action: SheetAction) {
    onClose();
    action.onSelect();
  }
</script>

<MobileSheet {open} {onClose} direction="bottom" {title} {label}>
  <div class="action-list" role="menu" aria-label={title ?? label}>
    {#each actions as action (action.label)}
      <button
        type="button"
        class="action-row"
        class:danger={action.danger}
        role="menuitem"
        onclick={() => select(action)}
      >
        {#if action.icon}
          <Icon name={action.icon} size={18} />
        {/if}
        <span>{action.label}</span>
      </button>
    {/each}
  </div>
</MobileSheet>

<style>
  .action-list {
    display: flex;
    flex-direction: column;
    padding: 0.25rem 0 0.5rem;
  }

  .action-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 3rem;
    padding: 0 1rem;
    font-family: inherit;
    font-size: 1rem;
    color: #1f2937;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    --icon-color: #6b7280;
  }

  .action-row + .action-row {
    border-top: 1px solid #f3f4f6;
  }

  .action-row:active {
    background: #f3f4f6;
  }

  .action-row:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }

  .action-row.danger {
    color: #dc2626;
    --icon-color: #dc2626;
  }
</style>
